import React, {useState, useEffect, useRef} from 'react';
import styles from '../tables.module.css';
import {PRISMAX_BACKEND_URL} from "../../../configs/config";

const FleetPurchaseInfoTable = () => {
    const [fleetData, setFleetData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const itemsPerPage = 10;
    const isInitialLoad = useRef(true);

    useEffect(() => {
        const fetchFleetData = async () => {
            try {
                setLoading(true);
                const token = localStorage.getItem("prismax_admin_token");
                const response = await fetch(
                    `${PRISMAX_BACKEND_URL}/api/admin/get-fleet-purchase-info?page=${currentPage}&page_size=${itemsPerPage}`,
                    {
                        headers: {
                            "Authorization": `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                    }
                );
                if (!response.ok) {
                    throw new Error('Failed to fetch fleet purchase information');
                }
                const data = await response.json();
                if (data.success && data.data) {
                    setFleetData(data.data);
                    setTotalPages(data.total_pages || 1);
                } else {
                    throw new Error('Invalid data format');
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
                isInitialLoad.current = false;
            }
        };

        fetchFleetData();
    }, [currentPage]);

    // Change page
    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    // Go to previous page
    const goToPreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    // Go to next page
    const goToNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    if (loading && isInitialLoad.current) {
        return <div className={styles.loading}>Loading fleet purchase data...</div>;
    }

    if (error) {
        return <div className={styles.error}>Error: {error}</div>;
    }

    return (
        <div className={styles.tableContainer}>
            <h2 className={styles.tableTitle}>Fleet Purchase Interest Records</h2>
            <div className={styles.tableWrapper}>
                <table className={styles.table}>
                    <thead>
                    <tr>
                        <th>Fleet Purchase ID</th>
                        <th>User ID</th>
                        <th>Robot Name</th>
                        <th>First Name</th>
                        <th>Last Name</th>
                        <th>Phone Number</th>
                        <th>Telegram</th>
                        <th>Project/Company</th>
                        <th>Location</th>
                        <th>Email</th>
                        <th>Created At</th>
                        <th>Email Sent</th>
                    </tr>
                    </thead>
                    <tbody>
                    {fleetData.length > 0 ? (
                        fleetData.map((item) => (
                            <tr key={item.fleet_purchase_id}>
                                <td>{item.fleet_purchase_id}</td>
                                <td>{item.user_id}</td>
                                <td>{item.robot_name}</td>
                                <td>{item.first_name}</td>
                                <td>{item.last_name}</td>
                                <td>{item.phone_number}</td>
                                <td>{item.telegram}</td>
                                <td>{item.project_company}</td>
                                <td>{item.location}</td>
                                <td>{item.email}</td>
                                <td>{new Date(item.created_at).toLocaleString()}</td>
                                <td>{item.email_sent ? 'True' : 'False'}</td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan="12" className={styles.noData}>
                                No fleet purchase information found
                            </td>
                        </tr>
                    )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {fleetData.length > 0 && (
                <div className={styles.pagination}>
                    <button
                        onClick={goToPreviousPage}
                        disabled={currentPage === 1}
                        className={`${styles.pageButton} ${currentPage === 1 ? styles.disabled : ''}`}
                    >
                        Previous
                    </button>

                    <div className={styles.pageNumbers}>
                        {Array.from({length: totalPages}, (_, i) => i + 1).map((number) => {
                            // Show limited page numbers with ellipsis for better UX
                            if (
                                number === 1 ||
                                number === totalPages ||
                                (number >= currentPage - 2 && number <= currentPage + 2)
                            ) {
                                return (
                                    <button
                                        key={number}
                                        onClick={() => paginate(number)}
                                        className={`${styles.pageNumber} ${currentPage === number ? styles.active : ''}`}
                                    >
                                        {number}
                                    </button>
                                );
                            } else if (
                                (number === currentPage - 3 && currentPage > 4) ||
                                (number === currentPage + 3 && currentPage < totalPages - 3)
                            ) {
                                return <span key={number} className={styles.ellipsis}>...</span>;
                            }
                            return null;
                        })}
                    </div>

                    <button
                        onClick={goToNextPage}
                        disabled={currentPage === totalPages}
                        className={`${styles.pageButton} ${currentPage === totalPages ? styles.disabled : ''}`}
                    >
                        Next
                    </button>

                </div>
            )}
        </div>
    );
};

export default FleetPurchaseInfoTable;
